import { toast } from '@cencosud-cencommerce/paris-uikit';
import type { Status } from '@cencosud-cencommerce/paris-uikit/dist/atoms/Inputs/TextField/partials/TextFieldBase';
import type { AsyncThunkOptions } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';

import { store } from '@/Infrastructure/State';
import { getBuyNowAlternativePaymentsById } from '@/modules/buy-now/application/getBuyNowAlternativePayments';
import { getBuyNowCartById } from '@/modules/buy-now/application/getBuyNowCart';
import { getBuyNowPaymentMethods } from '@/modules/buy-now/application/getBuyNowPaymentMethods';
import type BuyNowRepository from '@/modules/buy-now/domain/BuyNowRepository';
import type {
  CreateOrderPayload,
  OrderResponse
} from '@/modules/buy-now/domain/types';
import { addBuyNowDiscountCode } from '@/modules/buy-now/infrastructure/services/addBuyNowDiscount.mutations';
import { createBuyNowOrder } from '@/modules/buy-now/infrastructure/services/createBuyNowOrder.mutations';
import { getShippingGrouping as getShippingGroupingService } from '@/modules/buy-now/infrastructure/services/getShippingGrouping.queries';
import { removeBuyNowDiscountCode } from '@/modules/buy-now/infrastructure/services/removeBuyNowDiscount.mutations';
import { setOnBehalf as setOnBehalfService } from '@/modules/buy-now/infrastructure/services/setOnBehalf.mutations';
import { setShippingMethod } from '@/modules/buy-now/infrastructure/services/setShippingMethod.mutations';
import { setBuyNowCouponData } from '@/modules/buy-now/infrastructure/slices';
import type { AuthorizedPerson, Cart } from '@/modules/cart/domain/Cart';
import { EDiscoutCodeState } from '@/modules/cart/domain/Cart';
import type { CartPayments } from '@/modules/cart/domain/CartPayments';
import { httpCartDtoToCart } from '@/modules/cart/infrastructure/mappers/httpCartDtoToCart';
import { addToSaveForLater as addProductToSaveForLater } from '@/modules/cart/services/api/mutation/index';
import type { IAvailablePaymentMethodsResponse } from '@/modules/perseo-billing/interfaces';
import type { TErrorResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  retryWithDelay,
  shouldRetryError
} from '@/modules/shared/infrastructure/utils/retryWithDelay';
import type { shippingMethodBodyRequest } from '@/modules/shippingMethod/domain/ShippingMethodRepository';
import type {
  HttpShippingMethodDraft,
  HttpShippingMethodDto
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';

interface CreateAsyncOptions extends AsyncThunkOptions {
  rejectValue: {
    error: TErrorResponse;
    statusCode: number;
    retryCount: number;
  };
}

const GET_BUY_NOW_CART = 'buyNow/getCart';
const GET_SHIPPING_GROUPING = 'buyNow/getShippingGrouping';
const GET_BUY_NOW_PAYMENT_METHODS = 'buyNow/getPaymentMethods';
const SET_SHIPPING_METHOD = 'buyNow/setShippingMethod';
const ADD_BUY_NOW_DISCOUNT = 'buyNow/addDiscount';
const REMOVE_BUY_NOW_DISCOUNT = 'buyNow/removeDiscount';
const GET_BUY_NOW_ALTERNATIVE_PAYMENTS = 'buyNow/getAlternativePayments';
const CREATE_BUY_NOW_ORDER = 'buyNow/createOrder';
const SET_ON_BEHALF = 'buyNow/setOnBehalf';
const ADD_SAVE_FOR_LATER = 'cart/addSaveForLater';

const retryConfig = {
  maxRetries: 2,
  shouldRetry: shouldRetryError
};

export const createBuyNowRepository = (): BuyNowRepository => ({
  getCart: async (cartId: string) => {
    const { payload } = await store.dispatch(getBuyNowCart(cartId));
    return payload as Cart;
  },
  getShippingGrouping: async (
    cartId: string,
    params?: shippingMethodBodyRequest[]
  ) => {
    const { payload } = await store.dispatch(
      getShippingGrouping({ params, cartId })
    );
    return payload as HttpShippingMethodDto;
  },
  setShippingMethod: async (
    cartId: string,
    shippingMethodDraft: HttpShippingMethodDraft[]
  ) => {
    const { payload } = await store.dispatch(
      selectShippingMethod({ shippingMethod: shippingMethodDraft, cartId })
    );
    return payload as Cart;
  },
  getAvailablePaymentMethods: async () => {
    const { payload } = await store.dispatch(
      getBuyNowAvailablePaymentMethods()
    );
    return payload as string[];
  },
  getAlternativePayments: async (cartId: string) => {
    const { payload } = await store.dispatch(
      getBuyNowAlternativePayments(cartId)
    );
    return payload as CartPayments;
  },
  setOnBehalf: async (
    cartId: string,
    deliveryGroupId: string,
    authorizedPerson: AuthorizedPerson,
    isRegisteredUser: boolean = false
  ) => {
    const { payload } = await store.dispatch(
      setOnBehalf({
        cartId,
        deliveryGroupId,
        authorizedPerson,
        isRegisteredUser
      })
    );
    return payload as Cart;
  },
  addToSaveForLater: async (sku: string) => {
    const { payload } = await store.dispatch(addToSaveForLater({ sku }));
    return payload as Cart;
  }
});

export const getBuyNowCart = createAsyncThunk<Cart, string, CreateAsyncOptions>(
  GET_BUY_NOW_CART,
  async (cartId: string, { rejectWithValue }) => {
    const result = await retryWithDelay(
      () => getBuyNowCartById(cartId),
      retryConfig
    );

    if (result.error) {
      const axiosError = result.error as AxiosError;
      const errorInfo = axiosError?.response?.data as TErrorResponse;
      const statusCode = result.statusCode || 500;
      const retryCount = result.retryCount;

      console.error(
        `Error on getting cart: ${cartId} (Status: ${statusCode}, Retries: ${retryCount})`,
        result.error
      );

      return rejectWithValue({ error: errorInfo, statusCode, retryCount });
    }

    return result.data!;
  },
  {
    condition: (_cartId, { getState }) => {
      const state = getState() as { buyNow: { cart: { isLoading: boolean } } };
      // Skip if already loading
      if (state.buyNow.cart.isLoading) {
        return false;
      }
      return true;
    }
  }
);

export const getShippingGrouping = createAsyncThunk<
  HttpShippingMethodDto,
  { params?: shippingMethodBodyRequest[]; cartId: string },
  CreateAsyncOptions
>(
  GET_SHIPPING_GROUPING,
  async (
    {
      params,
      cartId
    }: { params?: shippingMethodBodyRequest[]; cartId: string },
    { rejectWithValue }
  ) => {
    const result = await retryWithDelay(
      () => getShippingGroupingService(params, cartId),
      retryConfig
    );

    if (result.error) {
      const axiosError = result.error as AxiosError;
      const errorInfo = axiosError?.response?.data as TErrorResponse;
      const statusCode = result.statusCode || 500;
      const retryCount = result.retryCount;

      console.error(
        `Error on getting buy now shipping methods (Status: ${statusCode}, Retries: ${retryCount})`,
        result.error
      );

      return rejectWithValue({ error: errorInfo, statusCode, retryCount });
    }

    return result.data!.data;
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as {
        buyNow: { shippingGrouping: { isLoading: boolean } };
      };
      if (state.buyNow.shippingGrouping.isLoading) {
        return false;
      }
      return true;
    }
  }
);

export const getBuyNowAvailablePaymentMethods = createAsyncThunk<
  IAvailablePaymentMethodsResponse,
  void,
  { rejectValue: TErrorResponse }
>(
  GET_BUY_NOW_PAYMENT_METHODS,
  async (_, { rejectWithValue }) => {
    try {
      const paymentMethods = await getBuyNowPaymentMethods();
      return paymentMethods;
    } catch (error) {
      const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
      console.error('Error fetching available payment methods', error);
      return rejectWithValue(errorInfo);
    }
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as {
        buyNow: { paymentMethods: { isLoading: boolean } };
      };
      if (state.buyNow.paymentMethods.isLoading) {
        return false;
      }
      return true;
    }
  }
);

export const selectShippingMethod = createAsyncThunk(
  SET_SHIPPING_METHOD,
  async ({
    shippingMethod,
    cartId
  }: {
    shippingMethod: HttpShippingMethodDraft[];
    cartId: string;
  }) => {
    const cartWithShippingMethod = await setShippingMethod(
      shippingMethod,
      cartId
    );
    return httpCartDtoToCart(cartWithShippingMethod.data);
  }
);

interface ApiError {
  error?: {
    type?: string;
    message?: string;
    code?: number;
  };
}

export const addBuyNowDiscount = createAsyncThunk<
  Cart,
  { discountCode: string; cartId: string },
  { rejectValue: TErrorResponse }
>(
  ADD_BUY_NOW_DISCOUNT,
  async ({ discountCode, cartId }, { dispatch, rejectWithValue }) => {
    try {
      const cart = await addBuyNowDiscountCode(discountCode, cartId);

      const invalidCoupon = cart.data?.discountCodes.find(
        (coupon) => coupon.state === EDiscoutCodeState.DoesNotMatchCart
      );

      if (invalidCoupon) {
        const couponId = invalidCoupon.discountCode.id;
        const previousCart = await removeBuyNowDiscountCode(couponId, cartId);

        toast({
          title: 'El cupón no aplica para tu carro',
          body: 'Este descuento aplica solo a algunos productos. Revisa sus requisitos.',
          position: 'bottom-left',
          status: 'error'
        });

        return httpCartDtoToCart(previousCart.data);
      }

      toast({
        title: '¡Listo! Agregaste un cupón',
        body: 'Solo se puede usar uno por compra.',
        position: 'bottom-left',
        status: 'success'
      });

      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorTyped = error as AxiosError<ApiError>;
      const errorType = errorTyped?.response?.data?.error?.type;
      if (errorType === EDiscoutCodeState.DiscountCodeNonApplicable) {
        const couponErrorData = {
          status: 'error' as Status,
          value: discountCode,
          helperText: 'Cupón inválido',
          buttonDisabled: true
        };

        dispatch(setBuyNowCouponData(couponErrorData));
      }
      toast({
        title: 'No pudimos agregar el cupón',
        body: 'Por favor, intenta de nuevo.',
        position: 'bottom-left',
        status: 'error'
      });
      return rejectWithValue(errorTyped.response?.data as TErrorResponse);
    }
  }
);

export const removeBuyNowDiscount = createAsyncThunk<
  Cart,
  { discountCode: string; cartId: string },
  { rejectValue: TErrorResponse }
>(
  REMOVE_BUY_NOW_DISCOUNT,
  async ({ discountCode, cartId }, { rejectWithValue }) => {
    try {
      const cart = await removeBuyNowDiscountCode(discountCode, cartId);

      toast({
        title: '¡Listo! Eliminaste un cupón',
        body: 'Recuerda que podrás volver a ingresarlo.',
        position: 'bottom-left',
        status: 'success'
      });

      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorTyped = error as AxiosError<ApiError>;
      return rejectWithValue(errorTyped.response?.data as TErrorResponse);
    }
  }
);

export const getBuyNowAlternativePayments = createAsyncThunk<
  CartPayments,
  string,
  CreateAsyncOptions
>(
  GET_BUY_NOW_ALTERNATIVE_PAYMENTS,
  async (cartId: string, { rejectWithValue }) => {
    const result = await retryWithDelay(
      () => getBuyNowAlternativePaymentsById(cartId),
      retryConfig
    );

    if (result.error) {
      const axiosError = result.error as AxiosError;
      const errorInfo = axiosError?.response?.data as TErrorResponse;
      const statusCode = result.statusCode || 500;
      const retryCount = result.retryCount;

      console.error(
        `Error on getting buy now alternative payments (Status: ${statusCode}, Retries: ${retryCount})`,
        result.error
      );

      return rejectWithValue({ error: errorInfo, statusCode, retryCount });
    }

    return result.data as CartPayments;
  },
  {
    condition: (_cartId, { getState }) => {
      const state = getState() as {
        buyNow: { alternativePayments: { isLoading: boolean } };
      };
      if (state.buyNow.alternativePayments.isLoading) {
        return false;
      }
      return true;
    }
  }
);

export const setOnBehalf = createAsyncThunk<
  Cart,
  {
    cartId: string;
    deliveryGroupId: string;
    authorizedPerson: AuthorizedPerson;
    isRegisteredUser?: boolean;
  },
  { rejectValue: TErrorResponse }
>(
  SET_ON_BEHALF,
  async (
    { cartId, deliveryGroupId, authorizedPerson, isRegisteredUser = false },
    { rejectWithValue }
  ) => {
    try {
      const cart = await setOnBehalfService(
        deliveryGroupId,
        authorizedPerson,
        cartId,
        isRegisteredUser
      );
      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
      console.error('Error on setting on behalf for buy now', error);
      return rejectWithValue(errorInfo);
    }
  }
);

export const createBuyNowOrderAsync = createAsyncThunk<
  OrderResponse,
  { payload: CreateOrderPayload; cartId: string },
  { rejectValue: TErrorResponse }
>(CREATE_BUY_NOW_ORDER, async ({ payload, cartId }, { rejectWithValue }) => {
  try {
    const response = await createBuyNowOrder(payload, cartId);
    return response.data;
  } catch (error) {
    const errorTyped = error as AxiosError;
    console.error('Error on creating buy now order: %s', cartId, error);

    let errorData: TErrorResponse | undefined;

    if (errorTyped.response?.data) {
      errorData = errorTyped.response.data as TErrorResponse;
    } else if (errorTyped.request?.response) {
      // Handle Tweak extension mocked responses (data is in request.response)
      try {
        errorData =
          typeof errorTyped.request.response === 'string'
            ? JSON.parse(errorTyped.request.response)
            : errorTyped.request.response;
      } catch (parseError) {
        console.error('Error parsing request.response:', parseError);
      }
    }

    return rejectWithValue(errorData as TErrorResponse);
  }
});

export const addToSaveForLater = createAsyncThunk(
  ADD_SAVE_FOR_LATER,
  async ({ sku }: { sku: string }) => {
    const cart = await addProductToSaveForLater(sku);
    return httpCartDtoToCart(cart.data);
  }
);
